import { useEffect, useMemo, useState } from "react";
import { HelloProduct } from "../types/backend";
import { API } from "../api/products";
import { isJson } from "../utils/contentType";
import {
  findBgSettings,
  findProductConfigurations,
} from "../utils/appSettings";

interface UseConfigurationInput {
  product?: HelloProduct;
}

const apiManager = new API();

interface CustomStyles {
  fonts: {
    font_name: string;
    font_url: string;
  }[];
  custom_css: string;
}

export const useConfigurations = ({ product }: UseConfigurationInput) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [customStyles, setCustomStyles] = useState<CustomStyles | undefined>(
    undefined
  );

  useEffect(() => {
    if (!product) {
      return;
    }
    let appId = product.application;
    if (typeof appId !== "string") {
      appId = appId._id;
    }

    apiManager
      .fetchConfigurations(appId)
      .then((res) => {
        res._items.forEach((config) => {
          switch (config.key) {
            case "custom_styles":
              const stylesString = config.value;
              let stylesJSON: CustomStyles | undefined = undefined;
              if (isJson(stylesString)) {
                if (typeof stylesString === "string") {
                  stylesJSON = JSON.parse(stylesString);
                } else {
                  stylesJSON = stylesString;
                }
              }
              if (stylesJSON) {
                setCustomStyles(stylesJSON);
              }
              break;
          }
        });
      })
      .catch((err) => {
        setIsError(true);
        setErrorMessage(
          "Error while fetching application configurations from backend"
        );
        console.error(
          "Error while fetching application configurations from backend",
          err
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [JSON.stringify(product)]);

  const bgJSON = useMemo(() => {
    if (!product) {
      return undefined;
    }
    return findBgSettings(product);
  }, [JSON.stringify(product)]);

  const productConfigurations = useMemo(() => {
    if (!product) {
      return undefined;
    }
    return findProductConfigurations(product);
  }, [JSON.stringify(product)]);

  return {
    isLoading: isLoading,
    isError: isError,
    error: errorMessage,
    stylesJSON: customStyles,
    bgJSON: bgJSON,
    productConfigurations: productConfigurations,
  };
};
